import React from 'react';

/**
 * Reviewer hint card showing Sharon's PHOCAS-derived metadata for a SKU.
 *
 * Currently driven by `phocas_sku_tags`. When that table is empty (the expected
 * initial state until a SKU→tag export is loaded), the backend returns an empty
 * `phocas_hint` array and this panel renders a "no PHOCAS mapping available"
 * message - graceful degradation, not an error.
 *
 * The data is reference-only: it does **not** influence the Akeneo categories
 * we push.
 */
const PhocasHintPanel = ({ sku, hints }) => {
  if (!hints || hints.length === 0) {
    return (
      <div className="card" style={{ marginTop: 12, background: '#0d1421' }}>
        <h3 style={{ marginTop: 0 }}>PHOCAS hint</h3>
        <p className="muted">
          No PHOCAS tag mapped for <code>{sku}</code>.
          {' '}
          Once a SKU→tag export is loaded into <code>phocas_sku_tags</code>, hints will
          appear here as a reviewer aid (Sharon&apos;s spreadsheet, reference only).
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 12, background: '#0d1421' }}>
      <h3 style={{ marginTop: 0 }}>PHOCAS hint{hints.length > 1 ? 's' : ''}</h3>
      {hints.map((h) => (
        <table key={`${h.product_tag_code}-${h.source || 'deterministic'}`} style={{ marginBottom: 8 }}>
          <tbody>
            <tr>
              <th style={{ textAlign: 'left', width: 140 }}>Tag</th>
              <td>
                <code>{h.product_tag_code}</code>
                {h.product_tag_description && <> — {h.product_tag_description}</>}
                {h.source === 'fuzzy' && (
                  <span className="status" style={{ marginLeft: 6, background: '#5a3a1a' }}>
                    fuzzy · confidence {Math.round((h.confidence || 0) * 100)}%
                  </span>
                )}
                {h.source === 'deterministic' && (
                  <span className="status" style={{ marginLeft: 6, background: '#1a4a3a' }}>
                    deterministic
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Segment</th>
              <td>{h.segment || <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Application</th>
              <td>{h.application || <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Product group / class</th>
              <td>{h.product_group_class || <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Sub category</th>
              <td>{h.sub_category || <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Model / family</th>
              <td>{h.model_family || <span className="muted">—</span>}</td>
            </tr>
            <tr>
              <th style={{ textAlign: 'left' }}>Brand (PHOCAS)</th>
              <td>{h.phocas_brand || <span className="muted">—</span>}</td>
            </tr>
            {h.comment && (
              <tr>
                <th style={{ textAlign: 'left' }}>Comment</th>
                <td>{h.comment}</td>
              </tr>
            )}
          </tbody>
        </table>
      ))}
      <p className="muted" style={{ marginTop: 4 }}>
        Reference only — these values are not pushed to Akeneo.
      </p>
    </div>
  );
};

export default PhocasHintPanel;
